<template>
  <div
    class="device"
    :style="deviceStyle"
    @pointerdown.stop="onPointerDown"
  >

    <!-- Botón borrar — anclado a la esquina superior derecha del ícono, sigue el resize -->
    <button
      v-if="!readonly && device.device_type !== 'dmp_panel_status' && device.device_type !== 'dmp_panel_action'"
      class="device-remove-btn"
      @pointerdown.stop
      @click.stop="$emit('remove', device)"
    >
      X
    </button>

    <div class="device-image-wrapper" :style="imgSize">

      <!-- Cono SVG -->
      <svg
        v-if="showCone && isCamera"
        class="cone-svg"
        :style="coneSvgStyle"
        @pointerdown.stop
      >
        <path
          :d="conePath"
          fill="rgba(0,200,80,0.25)"
          stroke="rgba(0,200,80,0.8)"
          stroke-width="1.5"
        />

        <circle
          :cx="coneDistanceHandle.x"
          :cy="coneDistanceHandle.y"
          r="6"
          fill="#00c850"
          stroke="white"
          stroke-width="1.5"
          style="cursor: pointer"
          @pointerdown.stop="onDistanceHandleDown"
        />

        <circle
          :cx="coneRotationHandle.x"
          :cy="coneRotationHandle.y"
          r="6"
          fill="#ff9900"
          stroke="white"
          stroke-width="1.5"
          style="cursor: pointer"
          @pointerdown.stop="onRotationHandleDown"
        />

        <circle
          :cx="coneAngleHandle.x"
          :cy="coneAngleHandle.y"
          r="6"
          fill="#ffffff"
          stroke="#00c850"
          stroke-width="1.5"
          style="cursor: pointer"
          @pointerdown.stop="onAngleHandleDown"
        />
      </svg>

      <!-- Radio SVG -->
      <svg
        v-if="showRadius && showRadiusControl"
        class="cone-svg"
        :style="radiusSvgStyle"
        @pointerdown.stop
      >
        <circle
          :cx="radiusOrigin.x"
          :cy="radiusOrigin.y"
          :r="radiusSize"
          fill="rgba(120, 120, 120, 0.2)"
          stroke="rgba(120, 120, 120, 0.6)"
          stroke-width="1.5"
          stroke-dasharray="4 3"
          pointer-events="none"
        />
        <circle
          :cx="radiusHandlePos.x"
          :cy="radiusHandlePos.y"
          r="6"
          fill="#888"
          stroke="white"
          stroke-width="1.5"
          style="cursor: pointer"
          @pointerdown.stop="onRadiusHandleDown"
        />
      </svg>

      <!-- Pulso de alarma -->
      <div v-if="showAlarm" class="circles">
        <div class="circle1"></div>
        <div class="circle2"></div>
        <div class="circle3"></div>
      </div>

      <!-- Alarma con icon → CustomIcon -->
      <CustomIcon
        v-if="device.device_type === 'alarm' && device.icon"
        :name="device.icon"
        :size="position.width || 30"
        :color="device.color_icon || '#6c757d'"
        :style="rotationStyle"
        class="device-img"
      />

      <!-- alarm_bridge_device con tag/rule icon -->
      <CustomIcon
        v-else-if="device.device_type === 'alarm_bridge_device' && editorAlarmStyle.icon"
        :name="editorAlarmStyle.icon"
        :size="position.width || 30"
        :color="editorAlarmStyle.iconColor"
        :style="rotationStyle"
        class="device-img"
      />

      <!-- Alarma sin icon → acronym badge -->
      <div
        v-else-if="device.device_type === 'alarm'"
        class="device-img d-flex align-items-center justify-content-center"
        :style="{
          ...rotationStyle,
          backgroundColor: device.color || '#6c757d',
          borderRadius: '4px',
          fontSize: '8px',
          fontWeight: '700',
          color: device.text_color || 'white',
        }"
      >
        {{ device.acronym || '?' }}
      </div>

      <!-- DMP Panel Status -->
      <div
        v-else-if="device.device_type === 'dmp_panel_status'"
        class="dmp-status-card"
      >
        <div class="dmp-status-card__icon">
          <feather-icon
            v-if="device.icon"
            :icon="dmpStatusIcon"
            size="14"
            :style="{ color: device.value === 'Falla' ? '#cf1111' : '#6c757d' }"
          />
        </div>
        <div class="dmp-status-card__info">
          <span class="dmp-status-card__name">{{ device.name }}</span>
          <span
            class="dmp-status-card__value"
            :style="{ color: device.value === 'Falla' ? '#cf1111' : '#6c757d' }"
          >
            {{ device.value }}
          </span>
        </div>
        <button
          v-if="!readonly"
          class="device-remove-btn-dmp"
          @pointerdown.stop
          @click.stop="$emit('remove', device)"
        >
          X
        </button>
      </div>

      <!-- DMP Panel Action card -->
      <div
        v-else-if="device.device_type === 'dmp_panel_action'"
        class="dmp-action-card"
      >
        <div class="dmp-action-card__icon">
          <feather-icon
            v-if="device.icon"
            :icon="dmpActionIcon"
            size="16"
            :style="{ color: device.color_icon || '#6c757d' }"
          />
        </div>
        <span class="dmp-action-card__name">{{ device.name }}</span>
        <button
          v-if="!readonly"
          class="device-remove-btn-dmp"
          @pointerdown.stop
          @click.stop="$emit('remove', device)"
        >
          X
        </button>
      </div>

      <!-- Imagen normal -->
      <img
        v-else
        :src="setImage(device)"
        class="device-img"
        :style="rotationStyle"
        draggable="false"
      />

      <!-- Botón toggle cono -->
      <button
        v-if="isCamera && !readonly"
        class="cone-btn"
        @click.stop="showCone = !showCone"
        :title="showCone ? 'Ocultar visión' : 'Mostrar visión'"
      >
        {{ showCone ? '−' : '+' }}
      </button>

      <!-- Botón reset cono -->
      <button
        v-if="isCamera && showCone && !readonly"
        class="cone-reset-btn"
        @click.stop="resetCone"
        title="Resetear visión"
      >
        ↺
      </button>

      <!-- Botón toggle radio -->
      <button
        v-if="showRadiusControl && !readonly"
        class="radius-btn"
        @click.stop="showRadius = !showRadius"
        :title="showRadius ? 'Ocultar radio' : 'Mostrar radio'"
      >
        {{ showRadius ? '−' : '+' }}
      </button>

      <!-- Botón reset radio -->
      <button
        v-if="showRadiusControl && showRadius && !readonly"
        class="radius-reset-btn"
        @click.stop="resetRadius"
        title="Resetear radio"
      >
        ↺
      </button>

    </div>

    <!-- Label + controles anclados a ella -->
    <div v-if="showLabel && device.device_type !== 'dmp_panel_status' && device.device_type !== 'dmp_panel_action'" class="device-label-wrap">
      <div
        class="device-label"
        v-b-tooltip.hover.bottom="name"
        :style="device.device_type === 'alarm' ? {
          backgroundColor: device.color || 'rgba(0,0,0,0.55)',
          color: device.text_color || 'white'
        } : {}"
      >
        <span
          v-if="device.device_type !== 'alarm'"
          class="status-dot"
          :class="{ off: device.status === 'OFFLINE' }"
        ></span>
        <span
          class="device-label__name"
          :style="device.device_type === 'alarm' ? { color: device.text_color || 'white' } : {}"
        >
          {{ name }} 
        </span>
      </div>

      <!-- Handle resize -->
      <div
        v-if="!readonly"
        class="resize-handle"
        @pointerdown.stop="onResizeDown"
      />
    </div>

    <!-- Indicador de error -->
    <div
      v-if="hasError"
      class="device-error-badge"
      v-b-tooltip.hover.top="'Error al guardar este dispositivo'"
    >
      Error en el componente
    </div>

  </div>
</template>

<script>
import { VBTooltip } from "bootstrap-vue"
import setDeviceImage from "./useList.js"
import CustomIcon from '@/components/CustomIcon.vue'

const DEFAULT_SIZE = 30

export default {
  name: "Device",
  components: { CustomIcon },
  directives: {
    "b-tooltip": VBTooltip
  },

  props: {
    position:          { type: Object,  required: true  },
    name:              { type: String,  default: ""     },
    device:            { type: Object,  required: true  },
    scale:             { type: Number,  default: 1      },
    isCamera:          { type: Boolean, default: false  },
    showLabel:         { type: Boolean, default: true   },
    showRadiusControl: { type: Boolean, default: false  },
    readonly:          { type: Boolean, default: false  },
    showAlarm: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false },
  },

  data() {
    return {
      showCone:     this.position?.cone?.visible   ?? false,
      coneDistance: this.position?.cone?.distance  ?? 80,
      coneAngle:    this.position?.cone?.angle     ?? 45,
      showRadius:   this.position?.radius?.visible ?? false,
      radiusSize:   this.position?.radius?.size    ?? 50,
    }
  },

  watch: {
    showCone(val) {
      this.$emit('update-cone', { device: this.device, visible: val, distance: this.coneDistance, angle: this.coneAngle })
    },
    coneDistance(val) {
      this.$emit('update-cone', { device: this.device, visible: this.showCone, distance: val, angle: this.coneAngle })
    },
    coneAngle(val) {
      this.$emit('update-cone', { device: this.device, visible: this.showCone, distance: this.coneDistance, angle: val })
    },
    showRadius(val) {
      this.$emit('update-radius', { device: this.device, visible: val, size: this.radiusSize })
    },
    radiusSize(val) {
      this.$emit('update-radius', { device: this.device, visible: this.showRadius, size: val })
    }
  },

  computed: {
    editorAlarmStyle() {
      const alarm = this.device.latest_alarm || null
      if (!alarm) return { iconColor: '#9e9e9e', icon: null }

      const tagIcon   = alarm.tag  && alarm.tag.icon       ? alarm.tag.icon       : null
      const tagColor  = alarm.tag  && alarm.tag.icon_color  ? alarm.tag.icon_color  : null
      const ruleIcon  = alarm.rule && alarm.rule.icon       ? alarm.rule.icon       : null
      const ruleColor = alarm.rule && alarm.rule.color      ? alarm.rule.color      : null

      if (tagIcon) {
        return { iconColor: tagColor || '#9e9e9e', icon: tagIcon }
      } else {
        return { iconColor: ruleColor || '#9e9e9e', icon: ruleIcon || null }
      }
    },
    deviceStyle() {
      return { left: `${this.position.x}px`, top: `${this.position.y}px` }
    },

    dmpStatusIcon() {
      if (!this.device.icon) return 'ActivityIcon'
      // "feather icon-shield" → "ShieldIcon"
      // "feather icon-battery-charging" → "BatteryChargingIcon"
      const raw = this.device.icon.replace('feather icon-', '')
      // Convertir kebab-case a PascalCase + Icon
      return raw
        .split('-')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join('') + 'Icon'
    },

    dmpActionIcon() {
      if (!this.device.icon) return 'ZapIcon'
      const raw = this.device.icon.replace('feather icon-', '')
      return raw
        .split('-')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join('') + 'Icon'
    },

    imgSize() {
      const w = this.position.width  || DEFAULT_SIZE
      const h = this.position.height || DEFAULT_SIZE
      return { width: `${w}px`, height: `${h}px` }
    },

    rotationStyle() {
      return { transform: `rotate(${this.position.degree || 0}deg)` }
    },

    coneSvgSize() {
      return this.coneDistance + 20
    },

    coneSvgStyle() {
      const w    = this.position.width  || DEFAULT_SIZE
      const h    = this.position.height || DEFAULT_SIZE
      const size = this.coneSvgSize
      return {
        position:      'absolute',
        left:          `${w / 2 - size}px`,
        top:           `${h / 2 - size}px`,
        width:         `${size * 2}px`,
        height:        `${size * 2}px`,
        overflow:      'visible',
        pointerEvents: 'none',
        zIndex:        5,
      }
    },

    coneOrigin() {
      const size = this.coneSvgSize
      return { x: size, y: size }
    },

    coneBaseAngle() {
      return ((this.position.degree || 0) - 90) * (Math.PI / 180)
    },

    conePath() {
      const { x: ox, y: oy } = this.coneOrigin
      const r        = this.coneDistance
      const half     = (this.coneAngle / 2) * (Math.PI / 180)
      const base     = this.coneBaseAngle

      if (this.coneAngle >= 358) {
        return `M ${ox} ${oy - r} A ${r} ${r} 0 1 1 ${ox - 0.01} ${oy - r} Z`
      }

      const x1       = ox + r * Math.cos(base - half)
      const y1       = oy + r * Math.sin(base - half)
      const x2       = ox + r * Math.cos(base + half)
      const y2       = oy + r * Math.sin(base + half)
      const largeArc = this.coneAngle > 180 ? 1 : 0
      return `M ${ox} ${oy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`
    },

    coneDistanceHandle() {
      const { x: ox, y: oy } = this.coneOrigin
      const base = this.coneBaseAngle
      return {
        x: ox + this.coneDistance * Math.cos(base),
        y: oy + this.coneDistance * Math.sin(base),
      }
    },

    coneAngleHandle() {
      const { x: ox, y: oy } = this.coneOrigin
      const r    = this.coneDistance
      const half = (this.coneAngle / 2) * (Math.PI / 180)
      const base = this.coneBaseAngle
      return {
        x: ox + r * Math.cos(base + half),
        y: oy + r * Math.sin(base + half),
      }
    },

    coneRotationHandle() {
      const { x: ox, y: oy } = this.coneOrigin
      const base   = this.coneBaseAngle
      const offset = 20
      return {
        x: ox - offset * Math.cos(base),
        y: oy - offset * Math.sin(base),
      }
    },

    radiusSvgStyle() {
      const w    = this.position.width  || DEFAULT_SIZE
      const h    = this.position.height || DEFAULT_SIZE
      const size = this.radiusSize + 20
      return {
        position:      'absolute',
        left:          `${w / 2 - size}px`,
        top:           `${h / 2 - size}px`,
        width:         `${size * 2}px`,
        height:        `${size * 2}px`,
        overflow:      'visible',
        pointerEvents: 'none',
        zIndex:        4,
      }
    },

    radiusOrigin() {
      const size = this.radiusSize + 20
      return { x: size, y: size }
    },

    radiusHandlePos() {
      const { x: ox, y: oy } = this.radiusOrigin
      return { x: ox + this.radiusSize, y: oy }
    },
  },

  methods: {
    setImage(device) {
      return setDeviceImage(device)
    },

    onPointerDown(e) {
      if (this.readonly) return
      if (e.target.closest('button')) return
      e.preventDefault()
      const startMouseX = e.clientX
      const startMouseY = e.clientY
      const startPosX   = this.position.x
      const startPosY   = this.position.y

      const onMove = (ev) => {
        const dx = (ev.clientX - startMouseX) / this.scale
        const dy = (ev.clientY - startMouseY) / this.scale
        this.$emit("update-position", {
          device:   this.device,
          position: { ...this.position, x: startPosX + dx, y: startPosY + dy }
        })
      }
      const onUp = () => {
        window.removeEventListener("pointermove", onMove)
        window.removeEventListener("pointerup", onUp)
      }
      window.addEventListener("pointermove", onMove)
      window.addEventListener("pointerup", onUp)
    },

    onResizeDown(e) {
      e.preventDefault()
      const startMouseX = e.clientX
      const startMouseY = e.clientY
      const startWidth  = this.position.width  || DEFAULT_SIZE
      const startHeight = this.position.height || DEFAULT_SIZE
      const ratio       = startWidth / startHeight

      const onMove = (ev) => {
        const dx    = (ev.clientX - startMouseX) / this.scale
        const dy    = (ev.clientY - startMouseY) / this.scale
        const delta = (dx + dy) / 2
        const MAX_SIZE = 70
        this.$emit("update-position", {
          device:   this.device,
          position: {
            ...this.position,
            width:  Math.min(MAX_SIZE, Math.max(16, startWidth  + delta)),
            height: Math.min(MAX_SIZE, Math.max(16, startHeight + delta / ratio)),
          }
        })
      }
      const onUp = () => {
        window.removeEventListener("pointermove", onMove)
        window.removeEventListener("pointerup", onUp)
      }
      window.addEventListener("pointermove", onMove)
      window.addEventListener("pointerup", onUp)
    },

    onDistanceHandleDown(e) {
      e.preventDefault()
      const { x: ox, y: oy } = this.coneOrigin
      const svgEl = e.currentTarget.closest('svg')

      const onMove = (ev) => {
        const svgRect = svgEl.getBoundingClientRect()
        const mouseX  = (ev.clientX - svgRect.left) / this.scale
        const mouseY  = (ev.clientY - svgRect.top)  / this.scale
        const dx      = mouseX - ox
        const dy      = mouseY - oy
        const raw     = Math.sqrt(dx * dx + dy * dy)
        const slowFactor = 0.1
        this.coneDistance = Math.max(20, this.coneDistance + (raw - this.coneDistance) * slowFactor)
      }

      const onUp = () => {
        window.removeEventListener("pointermove", onMove)
        window.removeEventListener("pointerup", onUp)
      }
      window.addEventListener("pointermove", onMove)
      window.addEventListener("pointerup", onUp)
    },

    onAngleHandleDown(e) {
      e.preventDefault()
      const { x: ox, y: oy } = this.coneOrigin
      const svgRect = e.currentTarget.closest('svg').getBoundingClientRect()
      const base    = this.coneBaseAngle

      const onMove = (ev) => {
        const mouseX       = (ev.clientX - svgRect.left) / this.scale
        const mouseY       = (ev.clientY - svgRect.top)  / this.scale
        const dx           = mouseX - ox
        const dy           = mouseY - oy
        const angleToMouse = Math.atan2(dy, dx)
        let diff           = angleToMouse - base
        while (diff >  Math.PI) diff -= 2 * Math.PI
        while (diff < -Math.PI) diff += 2 * Math.PI
        this.coneAngle = Math.min(359, Math.max(5, Math.abs(diff) * 2 * (180 / Math.PI)))
      }
      const onUp = () => {
        window.removeEventListener("pointermove", onMove)
        window.removeEventListener("pointerup", onUp)
      }
      window.addEventListener("pointermove", onMove)
      window.addEventListener("pointerup", onUp)
    },

    onRotationHandleDown(e) {
      e.preventDefault()
      const { x: ox, y: oy } = this.coneOrigin
      const svgRect = e.currentTarget.closest('svg').getBoundingClientRect()

      const onMove = (ev) => {
        const mouseX  = (ev.clientX - svgRect.left) / this.scale
        const mouseY  = (ev.clientY - svgRect.top)  / this.scale
        const dx      = mouseX - ox
        const dy      = mouseY - oy
        const degree  = Math.atan2(dy, dx) * (180 / Math.PI) + 90
        this.$emit("update-rotation", { device: this.device, degree })
      }
      const onUp = () => {
        window.removeEventListener("pointermove", onMove)
        window.removeEventListener("pointerup", onUp)
      }
      window.addEventListener("pointermove", onMove)
      window.addEventListener("pointerup", onUp)
    },

    onWheelRotate(event) {
      let degree = this.position.degree || 0
      degree += event.deltaY > 0 ? -10 : 10
      this.$emit("update-rotation", { device: this.device, degree })
    },

    resetCone() {
      this.coneDistance = 80
      this.coneAngle    = 45
      this.$emit("update-rotation", { device: this.device, degree: 0 })
    },

    onRadiusHandleDown(e) {
      e.preventDefault()
      const { x: ox, y: oy } = this.radiusOrigin
      const svgEl = e.currentTarget.closest('svg')

      const onMove = (ev) => {
        const svgRect   = svgEl.getBoundingClientRect()
        const mouseX    = (ev.clientX - svgRect.left) / this.scale
        const mouseY    = (ev.clientY - svgRect.top)  / this.scale
        const dx        = mouseX - ox
        const dy        = mouseY - oy
        this.radiusSize = Math.max(10, Math.sqrt(dx * dx + dy * dy))
      }
      const onUp = () => {
        window.removeEventListener("pointermove", onMove)
        window.removeEventListener("pointerup", onUp)
      }
      window.addEventListener("pointermove", onMove)
      window.addEventListener("pointerup", onUp)
    },

    resetRadius() {
      this.radiusSize = 50
      this.showRadius = true
    }
  }
}
</script>

<style lang="scss" scoped>
.device {
  position: absolute;
  z-index: 10;
  cursor: grab;
  user-select: none;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.device:active { cursor: grabbing; }

/* Wrapper relativo para imagen + SVGs */
.device-image-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.device-img {
  width: 100%;
  height: 100%;
  display: block;
  pointer-events: none;
  position: relative; 
  z-index: 2;
}

.cone-svg {
  pointer-events: none;
}

.cone-svg circle {
  pointer-events: all;
}

.device-label-wrap {
  /* position: absolute + centrado propio para que NO influya en el
     ancho de .device (evita que el ícono se recentre cuando el
     nombre del dispositivo es más ancho que el ícono) */
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 3px;
  display: inline-flex;
}

.device-label {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 4px;
  padding: 1px 6px;
  white-space: nowrap;
  overflow: hidden;
  max-width: 120px;
}

.device-label__name {
  font-size: 10px;
  color: white;
  padding-left: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-dot {
  width: 7px;
  height: 7px;
  min-width: 7px;
  border-radius: 50%;
  background-color: #28a745;
  display: inline-block;
}

.status-dot.off { background-color: #dc3545; }

.cone-btn {
  position: absolute;
  top: -8px;
  left: -8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: #00c850;
  color: white;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.cone-btn:hover { background: #009940; }

.cone-reset-btn {
  position: absolute;
  top: -8px;
  left: -30px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: #ff9900;
  color: white;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.cone-reset-btn:hover { background: #cc7a00; }

.radius-btn {
  position: absolute;
  top: 14px;
  left: -8px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: #888;
  color: white;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.radius-btn:hover { background: #555; }

.radius-reset-btn {
  position: absolute;
  top: 14px;
  left: -30px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: #888;
  color: white;
  font-size: 11px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.radius-reset-btn:hover { background: #555; }

.device-remove-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: -8px;
  left: auto;
  right: -15px;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  border: none;
  background: red;
  color: white;
  font-size: 10px;
  cursor: pointer;
  z-index: 20;
}

.device-remove-btn:hover { background: #c00000; }

.resize-handle {
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 12px;
  height: 12px;
  background: #007bff;
  border-radius: 50%;
  cursor: nwse-resize;
  z-index: 11;
}

.resize-handle:hover {
  background: #0056b3;
  transform: scale(1.2);
}

.circles {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
  overflow: visible;
  > div {
    animation: growAndFade 3s infinite ease-out;
    background-color: red;
    border-radius: 50%;
    height: 100%;
    position: absolute;
    width: 100%;
    left: 0;  
    top: 0;
  }

  .circle1 { animation-delay: 0s; }
  .circle2 { animation-delay: 2s; }
  .circle3 { animation-delay: 3s; }
}

@keyframes growAndFade {
  0%   { opacity: .9; transform: scale(0); }
  100% { opacity: 0;  transform: scale(2.5); }
}

.device-image-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: visible;  
}

.dmp-status-card {
  position: relative;
  display: inline-flex;
  align-items: center;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 6px 10px;
  gap: 8px;
  width: max-content;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  cursor: default;
  white-space: nowrap;

  &__icon {
    width: 28px;
    height: 28px;
    background: #f5f5f5;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__info {
    display: flex;
    flex-direction: column;
    gap: 1px;
  }

  &__name {
    font-size: 11px;
    font-weight: 500;
    color: #333;
    line-height: 1.2;
  }

  &__value {
    font-size: 10px;
    font-weight: 400;
    line-height: 1.2;
  }
}

.device-remove-btn-dmp {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: none;
  background: #dc3545;
  color: white;
  font-size: 9px;
  cursor: pointer;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;

  &:hover { background: #c00000; }
}

.resize-handle-dmp {
  position: absolute;
  bottom: -5px;
  right: -5px;
  width: 10px;
  height: 10px;
  background: #007bff;
  border-radius: 50%;
  cursor: nwse-resize;
  z-index: 11;

  &:hover {
    background: #0056b3;
    transform: scale(1.2);
  }
}

.device-error-badge {
  position: absolute;
  top: -6px;
  left: -6px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #dc3545;
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 25;
  cursor: help;
  box-shadow: 0 0 0 2px white;
  animation: errorPulse 1.5s ease-in-out infinite;
}

@keyframes errorPulse {
  0%, 100% { box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(220,53,69,0); }
  50%       { box-shadow: 0 0 0 2px white, 0 0 0 4px rgba(220,53,69,0.4); }
}

.dmp-action-card {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 4px;
  gap: 4px;
  width: 75px;
  height: 64px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.08);
  cursor: default;
  white-space: nowrap;

  &__icon {
    width: 32px;
    height: 32px;
    background: #f5f5f5;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &__name {
    font-size: 11px;
    font-weight: 500;
    color: #333;
    line-height: 1.2;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 75px;
  }
}

</style>